import React, { useState } from 'react';
import { motion } from 'framer-motion';

export default function Contact() {
  const EMPTY = { name: '', email: '', phone: '', website: '', message: '', accept: false };
  const [formData, setFormData] = useState(EMPTY);
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error

  // Submissions go to Netlify Forms. The schema is registered from the hidden
  // stub form in index.html, because this is a client-rendered SPA and Netlify
  // only parses static HTML at deploy time. Field names must match that stub.
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;

    const body = new URLSearchParams({
      'form-name': 'contact',
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim(),
      website: formData.website.trim(),
      message: formData.message.trim(),
      accept: formData.accept ? 'yes' : 'no',
    });

    setStatus('sending');
    try {
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus('sent');
      setFormData(EMPTY);
    } catch {
      // The previous version popped an alert() claiming the message had been
      // sent and cleared the form. Nothing was ever sent, and the enquiry was
      // unrecoverable. Never confirm what was not accepted.
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="pt-24 pb-24 md:pt-32 md:pb-32 bg-[#111111] border-t border-[#222222]">
      <div className="container mx-auto px-8">
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <h5 className="text-brand-orange font-bold tracking-widest uppercase text-sm mb-4">Contact Us</h5>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Ask us anything</h2>
          <p className="text-gray-400">
            Don't leave your cybersecurity and compliance to chance. Take the first step toward a secure and worry-free future.
          </p>
        </div>

        <motion.div
          className="max-w-4xl mx-auto bg-[#1a1a1a] border border-[#2a2a2a] p-10 md:p-14 rounded-3xl shadow-2xl"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <form
            name="contact"
            method="POST"
            action="/"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            onSubmit={handleSubmit}
            className="space-y-7"
          >
            {/* Netlify routes the submission by form-name from the POST body. */}
            <input type="hidden" name="form-name" value="contact" />
            {/* Honeypot: named in data-netlify-honeypot above, hidden from people. */}
            <p className="hidden" aria-hidden="true">
              <label>
                Do not fill this in
                <input name="bot-field" tabIndex={-1} autoComplete="off" />
              </label>
            </p>
            {status === 'sent' && (
              <div role="status" className="rounded-xl border border-green-500/40 bg-green-500/10 px-5 py-4">
                <p className="text-sm text-white font-semibold">Thank you — your message has been sent.</p>
                <p className="text-sm text-gray-400 mt-1">We'll come back to you shortly.</p>
              </div>
            )}
            {status === 'error' && (
              <div role="alert" className="rounded-xl border border-brand-orange/50 bg-brand-orange/10 px-5 py-4">
                <p className="text-sm text-white">
                  That did not send. Please try again, or email{' '}
                  <a href="mailto:hello@minotaur-solutions.com" className="text-brand-orange underline">
                    hello@minotaur-solutions.com
                  </a>
                  .
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input type="text" name="name" aria-label="Name" placeholder="Name *" required className="w-full bg-[#111111] border border-[#2a2a2a] rounded-xl px-5 py-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange transition-colors" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} />
              <input type="email" name="email" aria-label="Email" placeholder="Email *" required className="w-full bg-[#111111] border border-[#2a2a2a] rounded-xl px-5 py-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange transition-colors" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} />
              <input type="tel" name="phone" aria-label="Phone" placeholder="Phone *" required className="w-full bg-[#111111] border border-[#2a2a2a] rounded-xl px-5 py-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange transition-colors" value={formData.phone} onChange={e => setFormData({ ...formData, phone: e.target.value })} />
              <input type="text" name="website" aria-label="Website" placeholder="Website" className="w-full bg-[#111111] border border-[#2a2a2a] rounded-xl px-5 py-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange transition-colors" value={formData.website} onChange={e => setFormData({ ...formData, website: e.target.value })} />
            </div>
            <textarea name="message" aria-label="Your message" placeholder="Your Message *" required rows="5" className="w-full bg-[#111111] border border-[#2a2a2a] rounded-xl px-5 py-4 text-white placeholder-gray-600 focus:outline-none focus:border-brand-orange focus:ring-1 focus:ring-brand-orange transition-colors resize-none" value={formData.message} onChange={e => setFormData({ ...formData, message: e.target.value })} />
            <label className="flex items-start gap-3 cursor-pointer group">
              <div className="relative flex items-center justify-center mt-1">
                <input type="checkbox" name="accept" required className="peer appearance-none w-5 h-5 border-2 border-gray-600 rounded bg-[#111111] checked:bg-brand-orange checked:border-brand-orange transition-colors cursor-pointer" checked={formData.accept} onChange={e => setFormData({ ...formData, accept: e.target.checked })} />
                <svg className="absolute w-3 h-3 text-white opacity-0 peer-checked:opacity-100 pointer-events-none" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <span className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                By submitting your message you agree to have your data stored and treated according to our Privacy Policy.
              </span>
            </label>
            <button
              type="submit"
              disabled={status === 'sending'}
              className="btn-primary w-full md:w-auto md:px-12 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {status === 'sending' ? 'Sending…' : 'Send Message'}
            </button>
          </form>
        </motion.div>
      </div>
    </section>
  );
}